import { Box, Button, Stack, Typography } from '@mui/material';
import { useGetTimetableStructuresQuery } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { PageHeader } from '@schooly/components/filters';
import { SchoolUserRole } from '@schooly/constants';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { PlusIcon, VirtualizedList } from '@schooly/style';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Outlet, useNavigate } from 'react-router-dom';

import MainLayout from '../../../components/uikit-components/MainLayout/MainLayout';
import { useSchool } from '../../../hooks/useSchool';
import { useTimetablePermissions } from '../../../hooks/useTimetablePermissions';
import { SchoolTimetableStructureCard } from './SchoolTimetableStructureCard';
import { SchoolTimetableStructuresEmptyStub } from './SchoolTimetableStructuresEmptyStub';
import { SchoolTimetableStructuresSkeleton } from './SchoolTimetableStructuresSkeleton';

export const SchoolTimetableStructures: FC = () => {
  const { schoolId = '' } = useSchool();
  const navigate = useNavigate();

  const { permissions } = useAuth();
  const isAdmin = permissions?.includes('school_admin');
  const { canManageTimetables } = useTimetablePermissions();

  const { ageGroups, isLoading: isLoadingAgeGroups } = useAgeGroups({
    schoolId,
    userType: SchoolUserRole.Student,
  });

  const onAddClick = useCallback(() => {
    navigate('/settings/timetable_structures/new');
  }, [navigate]);

  const onAddAgeGroupClick = useCallback(() => {
    navigate('/settings/age_groups');
  }, [navigate]);

  const { data, isLoading: isLoadingStructures } = useGetTimetableStructuresQuery(
    { school_id: schoolId },
    { enabled: !!schoolId, refetchOnMount: 'always' },
  );

  const structures = data?.results || [];
  const isEmpty = !structures.length;
  const hasNoAgeGroups = !ageGroups.length;
  const isLoading = isLoadingAgeGroups || isLoadingStructures;
  const showButton = !isLoading && !isEmpty && !hasNoAgeGroups;

  const renderContent = () => {
    if (isLoading) {
      return <SchoolTimetableStructuresSkeleton />;
    }

    if (hasNoAgeGroups) {
      return (
        <SchoolTimetableStructuresEmptyStub
          labelTextId="school-timetableStructures-NoStructures"
          descriptionLabelTextId="school-timetableStructures-NoAgeGroups"
          action={
            isAdmin ? (
              <Button startIcon={<PlusIcon />} onClick={onAddAgeGroupClick}>
                <FormattedMessage id="school-timetableStructures-AddAgeGroup" />
              </Button>
            ) : (
              <FormattedMessage id="timetable-structure-AddAgeGroupNoPermissions" />
            )
          }
        />
      );
    }

    if (isEmpty) {
      return (
        <SchoolTimetableStructuresEmptyStub
          labelTextId="school-timetableStructures-NoStructures"
          descriptionLabelTextId="school-timetableStructures-NoStructuresDescription"
          action={
            canManageTimetables ? (
              <Button startIcon={<PlusIcon />} onClick={onAddClick}>
                <FormattedMessage id="school-timetableStructures-AddStructure" />
              </Button>
            ) : undefined
          }
        />
      );
    }

    return (
      <Stack flex={1} ml={-2}>
        <VirtualizedList
          items={structures}
          renderItem={(_, structure) => (
            <Box pb={2} pl={2} key={structure.id}>
              <SchoolTimetableStructureCard
                structure={structure}
                schoolId={schoolId}
                onEdit={canManageTimetables ? () => navigate(structure.id) : undefined}
              />
            </Box>
          )}
        />
      </Stack>
    );
  };

  return (
    <MainLayout>
      <Stack gap={2.5} flex={1}>
        <PageHeader
          actionsContent={
            showButton && canManageTimetables ? (
              <Button startIcon={<PlusIcon />} onClick={onAddClick}>
                <FormattedMessage id="school-timetableStructures-CreateStructure" />
              </Button>
            ) : undefined
          }
          pageTitle={
            <Typography variant="h1">
              <FormattedMessage id="school-sections-TimetableStructure" />
            </Typography>
          }
        />

        {renderContent()}
      </Stack>

      <Outlet />
    </MainLayout>
  );
};
